import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, Paperclip, Pencil, Printer, Upload, History } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { api, errMsg, downloadBlob } from "../lib/api";
import { Btn, StatusBadge, Loading, ErrorBox, fmtDate, fmtVal } from "./common";
import ActionDialog from "./ActionDialog";
import RecordForm from "./RecordForm";
import { isVisible, groupSections, AuthImage, FamilyEditor } from "./ProfileFields";
import { FilePreview } from "./GovFields";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const noop = () => {};
const empty = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length);
function withHeadings(module, rec) {
  if (!module.fields.some((f) => f.section)) return module.fields.filter((f) => !f.hidden && !(f.origin && empty(rec.data[f.key])) && !(f.key === "author" && empty(rec.data.author)));
  return groupSections(module.fields.filter((f) => f.type !== "resident_ref" && (isVisible(f, rec.data) || rec.restricted_fields.includes(f.key))))
    .flatMap(([s, fs]) => [{ heading: s }, ...fs]);
}
function renderVal(module, rec, f) {
  if (f.type === "family") return <FamilyEditor module={module} record={rec} data={{}} setData={noop} disabled />;
  if (f.type === "image") return rec.data[f.key] ? <AuthImage url={`/m/${module.key}/records/${rec.id}/field-file/${f.key}`} alt={f.label} testid={`view-image-${f.key}`} /> : "—";
  const v = rec.data[f.key];
  const names = rec.resolved || {};
  if (f.type === "inhabitant" || (f.type === "option" && f.by_id)) return rec.data[f.display] || "—";
  if (f.type === "personnel" || f.type === "committee") return v ? names[v] || "Unavailable record" : "—";
  if (f.type === "personnel_multi") return v?.length ? v.map((x) => names[x] || "Unavailable record").join(", ") : "—";
  if (f.type === "labeled_files") return <FileList module={module} rec={rec} items={v || []} testid={`view-files-${f.key}`} />;
  if (f.type === "committees") return v?.length ? <ul className="space-y-0.5" data-testid="personnel-committees">{v.map((c) => <li key={c.committee_id}><b>{c.name}</b> — {c.role}</li>)}</ul> : <span className="text-slate-500" data-testid="personnel-committees-empty">No committee assignments</span>;
  return fmtVal(f, v);
}

function FileList({ module, rec, items, testid }) {
  if (!items.length) return "—";
  return (
    <ul className="space-y-2" data-testid={testid}>
      {items.map((x) => (
        <li key={x.file_id || x.id} className="flex items-center gap-3">
          <FilePreview url={`/m/${module.key}/records/${rec.id}/files/${x.file_id || x.id}`} mime={x.mime} filename={x.filename} />
          <div className="min-w-0"><p className="font-medium">{x.label || x.filename}</p>
            <p className="text-xs text-slate-500 truncate">{x.filename}{x.size ? ` · ${Math.ceil(x.size / 1024)} KB` : ""}</p>
            <button className="text-brand text-xs font-semibold underline" onClick={() => downloadBlob(`/m/${module.key}/records/${rec.id}/files/${x.file_id || x.id}`, x.filename).catch((e) => toast.error(errMsg(e)))}>Download</button></div>
        </li>
      ))}
    </ul>
  );
}

function Supplementary({ rec, reload }) {
  const { meta } = useApp();
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const canUpload = meta.user.role === "secretary" && ["approved", "amended"].includes(rec.status);
  const files = rec.files.filter((f) => f.purpose === "supplementary" || (f.purpose !== "legislative" && !f.label));
  const submit = async () => {
    const fd = new FormData(); fd.append("file", file); fd.append("label", label);
    setBusy(true);
    try { await api.post(`/ordinances/${rec.id}/supplementary`, fd); toast.success("Attachment uploaded"); setLabel(""); setFile(null); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="mt-6" data-testid="supplementary-section">
      <h3 className="overline-label flex items-center gap-1 mb-2"><Paperclip className="h-3 w-3" /> Supplementary attachments</h3>
      {files.length ? <FileList module={{ key: "ordinances" }} rec={rec} items={files} testid="supplementary-list" /> : <p className="text-sm text-slate-500">No supplementary attachments.</p>}
      {canUpload && (
        <div className="mt-3 rounded-md border p-3 space-y-2" data-testid="supplementary-upload">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Attachment label (required)" className="w-full rounded-md border px-3 py-2 text-sm" data-testid="supplementary-label-input" />
          <div className="flex items-center gap-2">
            {file && <FilePreview local={URL.createObjectURL(file)} mime={file.type} filename={file.name} />}
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium hover:bg-slate-50"><Upload className="h-3.5 w-3.5" />{file ? file.name : "Choose file"}
              <input type="file" accept="image/png,image/jpeg,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="supplementary-file-input" /></label>
            <Btn className="!py-1.5 text-xs" disabled={!file || !label.trim() || busy} onClick={submit} data-testid="supplementary-upload-btn">{busy ? "Uploading…" : "Upload attachment"}</Btn>
          </div>
        </div>
      )}
    </section>
  );
}

function DeletePersonnel({ rec, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/personnel/${rec.id}/delete`, { password: pw, version: rec.version }); toast.success("Personnel record marked as Deleted"); setOpen(false); onDone(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); setPw(""); }
  };
  return (
    <>
      <Btn variant="danger" className="!py-0.5 !px-2 text-xs" onClick={() => { setOpen(true); setOk(false); setErr(""); }} data-testid="delete-personnel-btn">Delete</Btn>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-personnel-dialog">
          <DialogHeader><DialogTitle>Delete {rec.title}?</DialogTitle>
            <DialogDescription>The status changes to Deleted. The inhabitant record, linked account, committee history and legislative records are kept. Enter your current password to confirm.</DialogDescription></DialogHeader>
          <input type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password" className="w-full rounded-md border px-3 py-2 text-sm" data-testid="delete-personnel-password" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="delete-personnel-confirm-check" />I confirm this personnel record should be marked as Deleted.</label>
          {err && <p className="text-sm text-red-700" data-testid="delete-personnel-error">{err}</p>}
          <div className="flex justify-end gap-2"><Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn variant="danger" disabled={!pw || !ok || busy} onClick={submit} data-testid="delete-personnel-submit">{busy ? "Deleting…" : "Delete record"}</Btn></div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Attachments({ module, rec, reload }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/m/${module.key}/records/${rec.id}/files`, fd);
      toast.success("File uploaded");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <section className="mt-6" data-testid="attachments-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="overline-label flex items-center gap-1"><Paperclip className="h-3 w-3" /> Attachments</h3>
        {module.can_create && (
          <>
            <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="attachment-file-input" />
            <Btn variant="outline" className="!py-1 text-xs" disabled={busy} onClick={() => ref.current.click()} data-testid="attachment-upload-btn"><Upload className="h-3 w-3" />{busy ? "Uploading…" : "Upload PDF/image"}</Btn>
          </>
        )}
      </div>
      {rec.files.length === 0 ? <p className="text-sm text-slate-500">No attachments.</p> : (
        <ul className="divide-y rounded-md border">
          {rec.files.map((f) => (
            <li key={f.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="truncate">{f.filename} <span className="text-xs text-slate-500">v{f.version} · {Math.ceil(f.size / 1024)} KB</span></span>
              <button className="text-brand text-xs font-semibold underline" onClick={() => downloadBlob(`/m/${module.key}/records/${rec.id}/files/${f.id}`, f.filename).catch((e) => toast.error(errMsg(e)))} data-testid={`attachment-download-${f.id}`}>Download</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Timeline({ rec, module }) {
  return (
    <section className="mt-6" data-testid="record-history">
      <h3 className="overline-label flex items-center gap-1 mb-3"><History className="h-3 w-3" /> History</h3>
      <ol className="border-l-2 border-slate-200 ml-1.5 space-y-4">
        {rec.history.map((h, i) => (
          <li key={i} className="pl-4 relative">
            <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-brand ring-2 ring-white" />
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="font-medium capitalize">{h.action.replace(/_/g, " ")}</span>
              {h.to && h.from !== h.to && <StatusBadge status={h.to} statuses={module.statuses} />}
            </div>
            <p className="text-xs text-slate-500">{h.actor} · {fmtDate(h.at)}</p>
            {h.note && <p className="text-sm text-slate-700 mt-1 bg-slate-50 rounded px-2 py-1">{h.note}</p>}
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function RecordDrawer({ module, recordId, onClose, onChanged }) {
  const [rec, setRec] = useState(null);
  const [error, setError] = useState("");
  const [action, setAction] = useState(null);
  const [editing, setEditing] = useState(false);
  const { meta } = useApp();

  const load = useCallback(async () => {
    setError("");
    try { setRec((await api.get(`/m/${module.key}/records/${recordId}`)).data); } catch (e) { setError(errMsg(e)); }
  }, [module.key, recordId]);

  useEffect(() => { if (recordId) { setRec(null); load(); } }, [recordId, load]);

  const changed = () => { load(); onChanged(); };
  const restricted = new Set(rec?.restricted_fields || []);
  const actions = module.transitions.filter((t) => rec?.allowed_actions.includes(t.action));

  return (
    <Sheet open={!!recordId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="record-detail-drawer">
        <SheetHeader>
          <SheetTitle className="pr-6">{rec?.title || "Record"}</SheetTitle>
          <SheetDescription asChild>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs" data-testid="drawer-ref-no">{rec?.ref_no}</span>
              {rec && <StatusBadge status={rec.status} statuses={module.statuses} testid="drawer-status" />}
              {rec && module.key === "personnel" && rec.status === "separated" && meta.user.role === "captain" && <DeletePersonnel rec={rec} onDone={changed} />}
            </div>
          </SheetDescription>
        </SheetHeader>
        {error && <div className="mt-4"><ErrorBox text={error} onRetry={load} /></div>}
        {!rec && !error && <Loading />}
        {rec && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-2" data-testid="drawer-actions">
              {actions.map((t) => (
                <Btn key={t.action} variant={t.tone === "danger" ? "danger" : t.tone === "warn" ? "warn" : "brand"} className="!py-1.5 text-xs" onClick={() => setAction(t.action)} data-testid={`action-btn-${t.action}`}>{t.label}</Btn>
              ))}
              {rec.can_edit && <Btn variant="outline" className="!py-1.5 text-xs" onClick={() => setEditing(true)} data-testid="edit-record-btn"><Pencil className="h-3 w-3" />Edit</Btn>}
              {module.printable.includes(rec.status) && (
                <Btn variant="outline" className="!py-1.5 text-xs" onClick={() => window.open(`/print/${module.key}/${rec.id}`, "_blank")} data-testid="print-record-btn"><Printer className="h-3 w-3" />Print document</Btn>
              )}
              {actions.length === 0 && !rec.can_edit && <p className="text-xs text-slate-500" data-testid="no-actions-note">No actions available for your role at this status.</p>}
            </div>
            <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-md border bg-slate-50/60 p-4" data-testid="record-fields">
              {withHeadings(module, rec).map((f) => f.heading ? <h4 key={f.heading} className="col-span-2 mt-2 border-b pb-1 text-sm font-semibold text-brand" data-testid={`view-section-${f.heading[0]}`}>{f.heading}</h4> : (
                <div key={f.key} className={["textarea", "family", "multiselect", "labeled_files", "personnel_multi", "committees"].includes(f.type) ? "col-span-2" : ""}>
                  <dt className="text-[11px] uppercase tracking-wide text-slate-500">{f.label}</dt>
                  <dd className="text-sm text-slate-900 whitespace-pre-wrap break-words" data-testid={`record-field-${f.key}`}>
                    {restricted.has(f.key) ? <span className="inline-flex items-center gap-1 text-slate-500"><Lock className="h-3 w-3" />Restricted</span> : renderVal(module, rec, f)}
                  </dd>
                </div>
              ))}
              <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">Created</dt><dd className="text-sm">{fmtDate(rec.created_at)}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">Last updated</dt><dd className="text-sm">{fmtDate(rec.updated_at)}</dd></div>
            </dl>
            {module.attachments && <Attachments module={module} rec={rec} reload={load} />}
            {module.key === "ordinances" && <Supplementary rec={rec} reload={load} />}
            <Timeline rec={rec} module={module} />
          </div>
        )}
        {action && rec && <ActionDialog module={module} record={rec} action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); changed(); }} />}
        {rec && <RecordForm module={module} open={editing} record={rec} onClose={() => setEditing(false)} onSaved={changed} />}
      </SheetContent>
    </Sheet>
  );
}
